const FENCE_PATTERN = /^\s*(```|~~~)/

/**
 * Adds Markdown fences only around high-confidence code-shaped paragraphs.
 * This runs after a durable assistant message lands, never on partial stream
 * deltas, so a paragraph cannot jump between prose and code while it arrives.
 */
export function normalizeImplicitCodeBlocks(content: string): string {
  if (!content.trim()) return content

  const lines = content.split('\n')
  const output: string[] = []
  let index = 0
  let fence: string | null = null

  while (index < lines.length) {
    const line = lines[index]
    const marker = line.match(FENCE_PATTERN)?.[1] ?? null
    if (marker) {
      fence = fence === marker ? null : (fence ?? marker)
      output.push(line)
      index += 1
      continue
    }

    if (fence || !line.trim()) {
      output.push(line)
      index += 1
      continue
    }

    const block: string[] = []
    let cursor = index
    while (cursor < lines.length && lines[cursor].trim() && !FENCE_PATTERN.test(lines[cursor])) {
      block.push(lines[cursor])
      cursor += 1
    }

    const language = detectLanguage(block)
    output.push(...(language ? [`\`\`\`${language}`, ...block, '```'] : block))
    index = cursor
  }

  return output.join('\n')
}

function detectLanguage(lines: string[]): string | null {
  const text = lines.join('\n').trim()
  if (!text) return null

  if (isJson(text)) return 'json'
  if (isHtml(text)) return 'html'
  if (isSql(text, lines)) return 'sql'
  if (isYaml(lines)) return 'yaml'
  if (looksLikeMarkdownProse(lines)) return null
  if (isPython(text, lines)) return 'python'
  if (isTypeScript(text, lines)) return 'typescript'
  if (isShell(lines)) return 'bash'

  return null
}

function looksLikeMarkdownProse(lines: string[]): boolean {
  const proseLines = lines.filter((line) => /[.!?]$/.test(line.trim()))
  const markdownLines = lines.filter((line) => /^\s*(?:#{1,6}\s|[-*+]\s|\d+[.)]\s|>\s)/.test(line))
  return proseLines.length > Math.max(1, lines.length / 2) || markdownLines.length > 0
}

function isJson(text: string): boolean {
  if (!((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']')))) {
    return false
  }

  try {
    const value: unknown = JSON.parse(text)
    return typeof value === 'object' && value !== null
  } catch {
    return false
  }
}

function isHtml(text: string): boolean {
  if (/^<!doctype\s+html/i.test(text) || /^<html(?:\s|>)/i.test(text)) return true
  const tags = text.match(/<\/?[a-z][^>]*>/gi) ?? []
  return tags.length >= 4 && /^<[a-z][^>]*>/i.test(text) && /<\/[a-z][^>]*>$/i.test(text)
}

function isSql(text: string, lines: string[]): boolean {
  if (!/^\s*(?:select|with|insert\s+into|update|delete\s+from|create\s+(?:table|view)|alter\s+table)\b/i.test(text)) {
    return false
  }
  return lines.length >= 2 && /\b(?:from|where|join|values|set|as)\b/i.test(text)
}

function isYaml(lines: string[]): boolean {
  if (lines.length < 3 || lines.some((line) => /[{};]/.test(line))) return false
  const yamlLines = lines.filter((line) => /^\s*(?:-\s+)?[\w.-]+:\s*(?:.*)?$/.test(line)).length
  return yamlLines >= 3 && yamlLines >= lines.length - 1
}

function isPython(text: string, lines: string[]): boolean {
  const declarations = lines.filter((line) => /^\s*(?:def|class)\s+\w+.*:\s*$/.test(line)).length
  const imports = lines.filter((line) => /^\s*(?:from\s+[\w.]+\s+import|import\s+[\w.]+)/.test(line)).length
  const indented = lines.filter((line) => /^\s{2,}\S/.test(line)).length
  return declarations > 0 && indented > 0 || imports > 0 && lines.length >= 3 && /\b(?:print|return|def|class)\b/.test(text)
}

function isTypeScript(text: string, lines: string[]): boolean {
  const declarations = lines.filter((line) => /^\s*(?:const|let|var|function|class|interface|type|import|export)\b/.test(line)).length
  return declarations >= 2 && /(?:=>|[{};]|\bfrom\s+['"])/.test(text)
}

function isShell(lines: string[]): boolean {
  if (lines.length < 2) return false
  const commands = lines.filter((line) => /^\s*(?:\$\s+|(?:sudo\s+)?(?:cd|ls|pwd|curl|wget|git|npm|pnpm|yarn|bundle|rails|docker|ssh)\b)/.test(line)).length
  return commands >= 2 && commands >= lines.length - 1
}
